import { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { useScrollReveal } from '../hooks/useScrollReveal.js';
import { useNavTone } from '../hooks/useNavTone.js';
import { openPlayStore } from '../lib/appUrl.js';
import './Advertisers.css';

import imgUnlime from '../assets/BB/pexels-unlime-8181296.jpg';
import imgIntrospectivedsgn from '../assets/BB/pexels-introspectivedsgn-35038788.jpg';
import imgKoramangala from '../assets/BB/pexels-pexels-user-2153528950-32765072.jpg';
import imgWhitefield from '../assets/BB/pexels-ehaan-deva-2149036462-32358148.jpg';

const MOBILE_LINKS = [
  { href: '/advertisers#value', label: 'What you get' },
  { href: '/advertisers#disc-preview', label: 'Discovery platform' },
  { href: '/advertisers#escrow', label: 'Escrow & payments' },
  { href: '/advertisers#formats', label: 'Formats' },
  { href: '/advertisers#faq', label: 'FAQ' },
];

const NAV_SECTIONS = [
  ['hero', 'dark'],
  ['problem', 'light'],
  ['booking-steps', 'light'],
  ['value', 'light'],
  ['disc-preview', 'dark'],
  ['escrow', 'dark'],
  ['formats', 'light'],
  ['faq', 'light'],
  ['cta', 'dark'],
];

const HERO_CARDS = [
  { img: imgUnlime, status: 'available', name: 'MG Road · Bengaluru', meta: 'Billboard · 40×20 ft', price: '₹42,000 / mo' },
  { img: imgIntrospectivedsgn, status: 'available', name: 'Indiranagar · Bengaluru', meta: 'Billboard · 30×15 ft', price: '₹35,000 / mo' },
  { img: imgKoramangala, status: 'booked', name: 'Koramangala · Bengaluru', meta: 'Unipole · 25×15 ft', price: '₹56,000 / mo' },
  { img: imgWhitefield, status: 'available', name: 'Whitefield · Bengaluru', meta: 'Billboard · 20×10 ft', price: '₹28,000 / mo', added: true },
];

const DISCOVERY_CARDS = [
  { img: imgUnlime, status: 'available', name: 'MG Road · Bengaluru', meta: '40×20 ft · Billboard', price: '₹42,000/mo' },
  { img: imgIntrospectivedsgn, status: 'available', name: 'Indiranagar · Bengaluru', meta: '30×15 ft · Billboard', price: '₹35,000/mo', selected: true },
  { img: imgKoramangala, status: 'booked', name: 'Koramangala · Bengaluru', meta: '25×15 ft · Unipole', price: '₹56,000/mo' },
];

const STEPS = [
  { num: '01', title: 'Search and shortlist', desc: 'Filter verified spaces by city, format, footfall, and budget. Every listing has real dimensions, photos, transparent pricing, and live availability. No calls, no brokers.' },
  { num: '02', title: 'Book and pay safely', desc: 'Select your dates and confirm the booking. Payment goes into FlipAd escrow — held securely until your campaign runs and proof is verified. You are protected from the moment you pay.' },
  { num: '03', title: 'Campaign runs, proof arrives', desc: 'Your creative goes live. At the end of the campaign, the space owner submits timestamped photos. FlipAd verifies them. You receive confirmed proof that your ad ran exactly as booked.' },
];

const VALUE_CARDS = [
  { icon: 'ph-rows', title: 'Transparent inventory', desc: 'Every space on FlipAd is listed with verified dimensions, real photos, declared footfall estimates, and fixed pricing. Compare sites side by side without a single phone call.' },
  { icon: 'ph-shield-check', title: 'Escrow-protected payment', desc: "Your payment is held by FlipAd until your campaign runs and proof of display is submitted and verified. If the campaign doesn't happen, neither does the payment. You are never exposed." },
  { icon: 'ph-seal-check', title: 'Verified proof of display', desc: 'Every campaign closes with a timestamped, geo-tagged photo submitted by the space owner and reviewed by FlipAd. Your finance team gets a clean paper trail. Your marketing team gets closure.' },
];

const FORMAT_TILES = [
  { icon: 'ph-signpost', name: 'Billboard', desc: 'Large roadside displays for maximum visibility. Available across national highways and city arterials.' },
  { icon: 'ph-broadcast', name: 'Unipole Hoarding', desc: 'Tall single-pole billboards for long-range visibility. Effective for brand building on highways and entry roads.' },
  { icon: 'ph-monitor-play', name: 'Digital Screen (DOOH)', desc: 'Dynamic screens for flexible, high-impact ads. Update creatives remotely without print or installation.' },
  { icon: 'ph-bus', name: 'Transit Panel', desc: 'Ads across buses, trains, and transit hubs. Reach commuters at high-dwell-time moments across the city.' },
  { icon: 'ph-bench', name: 'Street Furniture', desc: 'Eye-level ads on shelters and public fixtures. Pedestrian-facing, high frequency, neighbourhood-level targeting.' },
  { icon: 'ph-buildings', name: 'Commercial Spaces', desc: 'Walls, rooftops, and storefronts for custom branding. Large-format impact in premium commercial locations.' },
];

const FAQ_ITEMS = [
  {
    q: 'Who can book on FlipAd?',
    a: "Any brand, agency, or individual looking to run OOH advertising in India can book on FlipAd. You don't need an agency relationship or a minimum spend. Create a free account and start browsing immediately.",
  },
  {
    q: 'How is pricing set?',
    a: 'Space owners set their own rates and these are displayed transparently on every listing. There is no hidden negotiation — what you see is what you pay. Some premium formats allow a direct message to the space owner for custom campaign requirements.',
  },
  {
    q: 'How does escrow work for advertisers?',
    a: "When you confirm a booking, your payment is transferred to FlipAd escrow — not to the space owner. It is held securely until the campaign period ends and the space owner submits verified proof of display. Only then is payment released. If the campaign doesn't run, you are refunded.",
  },
  {
    q: 'What is proof of display?',
    a: 'At the end of your campaign, the space owner uploads a timestamped photo of your creative running on their site. FlipAd reviews the photo for accuracy and date validity before releasing payment and issuing your campaign completion report.',
  },
  {
    q: 'Can I book multiple sites for one campaign?',
    a: 'Yes. You can add multiple sites to a campaign plan, set the same or different dates per site, and manage everything from a single campaign dashboard. Useful for city-wide or multi-city campaigns.',
  },
  {
    q: "What happens if my ad doesn't go up?",
    a: 'If a space owner fails to display your creative during the confirmed campaign period, the escrow payment is returned to you in full. FlipAd investigates all disputes and space owners who fail to deliver are flagged on the platform.',
  },
];

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className={`so-faq-item${isOpen ? ' is-open' : ''}`}>
      <button className="so-faq-q" aria-expanded={isOpen} onClick={onToggle}>
        {item.q}
        <i className="ph ph-plus so-faq-icon" aria-hidden="true" />
      </button>
      <div className="so-faq-a">
        <div className="so-faq-a-inner">{item.a}</div>
      </div>
    </div>
  );
}

export default function Advertisers() {
  useScrollReveal();
  useNavTone(NAV_SECTIONS);

  const [openFaq, setOpenFaq] = useState(null);
  const escDiagRef = useRef(null);

  // Toggled via direct classList mutation, not React state — see the same
  // note in SpaceOwners.jsx's escrow effect: useScrollReveal also mutates
  // this element's classList imperatively (adding "in"), outside React's
  // render cycle, and a state-driven className here would have React's next
  // re-render silently wipe that out.
  useEffect(() => {
    const el = escDiagRef.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) {
      el.classList.add('esc-active');
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          el.classList.add('esc-active');
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="For Advertisers — Book Verified OOH Ad Spaces | FlipAd"
        description="Search verified billboards, digital screens, and transit sites across India. Transparent pricing, escrow-protected payments, and verified proof of display — no brokers, no calls."
        path="/advertisers"
      />
      <Header mobileLinks={MOBILE_LINKS} activeNavKey="advertisers" />

      {/* ════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════ */}
      <section className="so-hero" id="hero" aria-labelledby="adv-hero-hl">
        <div className="container">
          <div className="so-hero-inner">

            {/* Left: copy */}
            <div className="so-hero-left">
              <p className="eyebrow">For Advertisers</p>
              <h1 className="so-hero-hl" id="adv-hero-hl">
                Find the right space.<br />Book it today.<br />Know it ran.
              </h1>
              <p className="so-hero-sub">
                Search thousands of verified OOH spaces across India. Confirm in minutes,
                pay through escrow, and receive timestamped proof that your campaign actually ran.
              </p>
              <div className="so-hero-ctas">
                <button type="button" className="lmn-btn primary lg nav-auth-trigger" onClick={openPlayStore}>
                  Browse inventory <i className="ph ph-arrow-right" aria-hidden="true" />
                </button>
                {/* Not a plain <a href="#booking-steps">: under HashRouter that
                    native hash jump would overwrite the router's own URL
                    fragment, changing the "route" to /booking-steps (no such
                    route exists) instead of just scrolling within this page. */}
                <Link to="/advertisers#booking-steps" className="lmn-btn outline-white lg">
                  See how it works ↓
                </Link>
              </div>
              <Link
                to="/space-owners"
                style={{
                  display: 'inline-block',
                  marginTop: 'var(--lumen-space-4)',
                  fontSize: 'var(--lumen-text-sm)',
                  fontWeight: 600,
                  color: 'color-mix(in srgb,#fff 45%,transparent)',
                  textDecoration: 'none',
                  transition: 'color 150ms ease',
                }}
                onMouseOver={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}
                onMouseOut={(e) => { e.currentTarget.style.color = 'color-mix(in srgb,#fff 45%,transparent)'; }}
              >
                Own a space? List it on FlipAd →
              </Link>
            </div>

            {/* Right: discovery fragment */}
            <div className="so-hero-right" aria-hidden="true">
              <div className="mock-panel">
                <div className="mock-topbar">
                  <div className="mock-dots">
                    <div className="mock-dot mock-dot-r" />
                    <div className="mock-dot mock-dot-a" />
                    <div className="mock-dot mock-dot-g" />
                  </div>
                  <span className="mock-bar-label">FlipAd — Inventory Search</span>
                </div>
                <div className="mock-body">
                  <div className="mock-search-bar">
                    <i className="ph ph-magnifying-glass" style={{ fontSize: 13 }} />
                    <span>Bengaluru &nbsp;·&nbsp; Billboard &nbsp;·&nbsp; Apr 1–30</span>
                  </div>
                  <div className="adv-filters">
                    <span className="adv-chip"><i className="ph ph-circle" style={{ fontSize: 6 }} /> Available</span>
                    <span className="adv-chip">Under ₹50K</span>
                    <span className="adv-chip">Billboard</span>
                    <span className="adv-chip plain"><i className="ph ph-funnel" style={{ fontSize: 9 }} /> More</span>
                  </div>
                  <div className="adv-grid">
                    {HERO_CARDS.map((c) => (
                      <div className={`adv-card${c.added ? ' selected' : ''}`} key={c.name}>
                        <div className="adv-thumb" style={{ backgroundImage: `url(${c.img})` }}>
                          {c.added && (
                            <div className="adv-added-overlay">
                              <i className="ph-fill ph-check-circle" />
                              <span>ADDED TO CAMPAIGN</span>
                            </div>
                          )}
                        </div>
                        <div className="adv-info">
                          <div className="adv-name">{c.name}</div>
                          <div className="adv-meta">{c.meta}</div>
                          <div className="adv-card-tags">
                            <span className="adv-card-tag">{c.status === 'available' ? 'Available' : 'Booked'}</span>
                          </div>
                          <div className="adv-price-row">
                            <span className="adv-price">{c.price}</span>
                            <i className="ph ph-caret-right" aria-hidden="true" />
                          </div>
                        </div>
                        {c.added && (
                          <div className="adv-added-strip"><i className="ph-fill ph-check-circle" /> Added to campaign</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="adv-count">Showing 4 of 68 spaces in Bengaluru</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          PROBLEM — Advertiser perspective
      ════════════════════════════════════════════════ */}
      <section className="problem sec" id="problem" aria-labelledby="adv-prob-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 560, marginBottom: 'var(--lumen-space-10)' }}>
            <p className="eyebrow">The current reality</p>
            <h2 className="sec-title" id="adv-prob-hl">This is how OOH advertising works today.</h2>
            <p className="sec-desc">
              If you&apos;ve tried to plan an OOH campaign in India without a large agency,
              you already know what you&apos;re up against.
            </p>
          </div>

          <div className="adv-prob-cards">

            <div className="adv-prob-card reveal" data-d="1" role="group" aria-label="Problem 1 of 3">
              <div className="adv-prob-illus">
                <svg viewBox="0 0 260 190" width="260" height="190" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M50 40 L210 40 L160 100 L160 155 L100 155 L100 100 Z" fill="white" stroke="#e2e8f0" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="M50 40 L210 40 L165 95 L95 95 Z" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1" />
                  <line x1="75" y1="52" x2="185" y2="52" stroke="#e2e8f0" strokeWidth="1.5" />
                  <line x1="85" y1="64" x2="175" y2="64" stroke="#e2e8f0" strokeWidth="1.5" />
                  <line x1="95" y1="76" x2="165" y2="76" stroke="#e2e8f0" strokeWidth="1.5" />
                  <text x="116" y="120" fill="#f43f5e" fontSize="18" fontWeight="800" fontFamily="system-ui,sans-serif" opacity=".8">?</text>
                  <text x="130" y="138" fill="#f43f5e" fontSize="14" fontWeight="700" fontFamily="system-ui,sans-serif" opacity=".5">?</text>
                  <rect x="134" y="110" width="100" height="28" rx="14" fill="white" stroke="#fecdd3" strokeWidth="1.5" />
                  <text x="184" y="128" fill="#be123c" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif" textAnchor="middle">CALL FOR PRICING</text>
                  <rect x="25" y="140" width="92" height="28" rx="14" fill="white" stroke="#fecdd3" strokeWidth="1.5" />
                  <text x="71" y="158" fill="#be123c" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif" textAnchor="middle">TALK TO BROKER</text>
                </svg>
              </div>
              <div className="adv-prob-body">
                <p className="adv-prob-tag"><i className="ph-fill ph-warning-circle" aria-hidden="true" />Problem 1</p>
                <h3 className="prob-quote">&quot;The right sites for your campaign? Depends on who you know.&quot;</h3>
                <p className="adv-prob-desc">No verified inventory database, no transparent pricing, no way to compare sites side by side. The same space quotes differently depending on your agency, your relationship, and how well you negotiate.</p>
              </div>
            </div>

            <div className="adv-prob-card reveal" data-d="2" role="group" aria-label="Problem 2 of 3">
              <div className="adv-prob-illus">
                <svg viewBox="0 0 260 190" width="260" height="190" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="65" y="22" width="100" height="130" rx="8" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
                  <rect x="65" y="22" width="100" height="28" rx="8" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1" />
                  <rect x="65" y="38" width="100" height="12" fill="#f1f5f9" />
                  <text x="115" y="41" fill="#64748b" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif" textAnchor="middle">INVOICE</text>
                  <line x1="80" y1="62" x2="150" y2="62" stroke="#e2e8f0" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="80" y1="72" x2="140" y2="72" stroke="#e2e8f0" strokeWidth="1.5" strokeLinecap="round" opacity=".6" />
                  <line x1="80" y1="82" x2="148" y2="82" stroke="#e2e8f0" strokeWidth="1.5" strokeLinecap="round" opacity=".4" />
                  <rect x="69" y="94" width="122" height="32" rx="8" fill="#fee2e2" stroke="#f43f5e" strokeWidth="1.5" />
                  <text x="130" y="114" fill="#be123c" fontSize="11" fontWeight="800" fontFamily="system-ui,sans-serif" textAnchor="middle">100% ADVANCE</text>
                  <rect x="69" y="136" width="122" height="26" rx="13" fill="white" stroke="#fecdd3" strokeWidth="1.5" />
                  <text x="130" y="153" fill="#94a3b8" fontSize="8.5" fontWeight="600" fontFamily="system-ui,sans-serif" textAnchor="middle">RECEIPT? SORT IT LATER.</text>
                </svg>
              </div>
              <div className="adv-prob-body">
                <p className="adv-prob-tag"><i className="ph-fill ph-warning-circle" aria-hidden="true" />Problem 2</p>
                <h3 className="prob-quote">&quot;100% advance. Receipt? We&apos;ll sort that later.&quot;</h3>
                <p className="adv-prob-desc">Full payment upfront to vendors you&apos;ve never met, with no contract and no protection. If the campaign never goes up, recovering your money means starting a dispute you&apos;ll likely lose.</p>
              </div>
            </div>

            <div className="adv-prob-card reveal" data-d="3" role="group" aria-label="Problem 3 of 3">
              <div className="adv-prob-illus">
                <svg viewBox="0 0 260 190" width="260" height="190" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="50" y="58" width="130" height="90" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
                  <rect x="88" y="44" width="50" height="22" rx="5" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1.5" />
                  <circle cx="115" cy="103" r="28" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
                  <circle cx="115" cy="103" r="18" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1" />
                  <line x1="104" y1="92" x2="126" y2="114" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" opacity=".7" />
                  <line x1="126" y1="92" x2="104" y2="114" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" opacity=".7" />
                  <rect x="155" y="62" width="16" height="16" rx="3" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1" />
                  <text x="163" y="74" fill="#94a3b8" fontSize="10" fontWeight="700" fontFamily="system-ui,sans-serif" textAnchor="middle">⚡</text>
                  <rect x="120" y="120" width="116" height="28" rx="14" fill="white" stroke="#fecdd3" strokeWidth="1.5" />
                  <text x="178" y="138" fill="#be123c" fontSize="8.5" fontWeight="700" fontFamily="system-ui,sans-serif" textAnchor="middle">TRUST ME, IT&apos;S RUNNING</text>
                </svg>
              </div>
              <div className="adv-prob-body">
                <p className="adv-prob-tag"><i className="ph-fill ph-warning-circle" aria-hidden="true" />Problem 3</p>
                <h3 className="prob-quote">&quot;Trust me, it&apos;s been running since Monday.&quot;</h3>
                <p className="adv-prob-desc">No proof of display, no timestamps, no independent verification. Your ad may have run for 30 days, 3 days, or not at all — and you will never know for certain.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          HOW BOOKING WORKS
      ════════════════════════════════════════════════ */}
      <section className="so-steps sec" id="booking-steps" aria-labelledby="steps-hl">
        <div className="container">
          <div className="sec-header center reveal" style={{ maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
            <p className="eyebrow">The FlipAd flow</p>
            <h2 className="sec-title" id="steps-hl">From search to live campaign in minutes.</h2>
          </div>

          <div className="so-steps-row">
            {STEPS.map((s, i) => (
              <Fragment key={s.num}>
                {i > 0 && <div className="so-step-conn" aria-hidden="true"><div className="so-step-conn-line" /></div>}
                <div className="so-step-card reveal" data-d={String(i + 1)}>
                  <div className="so-step-num">{s.num}</div>
                  <div className="so-step-title">{s.title}</div>
                  <p className="so-step-desc">{s.desc}</p>
                </div>
              </Fragment>
            ))}
          </div>

          <div className="so-steps-claim reveal">
            <p className="so-steps-claim-text">Your money doesn&apos;t move<br />until your ad does.</p>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          WHAT YOU GET
      ════════════════════════════════════════════════ */}
      <section className="so-value sec" id="value" aria-labelledby="value-hl">
        <div className="container">
          <div className="sec-header center reveal" style={{ maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
            <p className="eyebrow">Why FlipAd</p>
            <h2 className="sec-title" id="value-hl">Book with certainty, every time.</h2>
          </div>

          <div className="so-value-grid">
            {VALUE_CARDS.map((v, i) => (
              <div className="so-value-card reveal" data-d={String(i + 1)} key={v.title}>
                <div className="so-value-icon"><i className={`ph ${v.icon}`} aria-hidden="true" /></div>
                <div className="so-value-title">{v.title}</div>
                <p className="so-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          DISCOVERY PREVIEW
      ════════════════════════════════════════════════ */}
      <section className="disc-preview sec" id="disc-preview" aria-labelledby="disc-hl">
        <div className="container">
          <div className="disc-preview-inner">

            {/* Left: section header */}
            <div className="disc-preview-left reveal">
              <p className="eyebrow">The platform</p>
              <h2 className="sec-title" id="disc-hl" style={{ color: '#fff' }}>
                Search like you&apos;d search<br />for a flight.
              </h2>
              <p className="sec-desc">
                Filter by city, format, budget, and availability. Every site has the
                information you need to make a decision without a single conversation.
              </p>
            </div>

            {/* Right: browser frame with discovery illustration */}
            <div className="reveal" data-d="1">
              <div className="so-browser">
                <div className="so-browser-bar">
                  <div className="so-browser-dot" />
                  <div className="so-browser-dot" />
                  <div className="so-browser-dot" />
                  <div className="so-browser-url">flipad.in/discover</div>
                </div>
                <div className="so-browser-body">

                  {/* Search bar with segments */}
                  <div className="disc-search">
                    <div className="disc-seg">
                      <i className="ph ph-map-pin" style={{ fontSize: 12, color: 'var(--b500)' }} />
                      <div><span className="disc-seg-lbl">Location</span><span className="disc-seg-val">Bengaluru</span></div>
                    </div>
                    <div className="disc-seg-div" />
                    <div className="disc-seg">
                      <i className="ph ph-signpost" style={{ fontSize: 12, color: 'var(--b500)' }} />
                      <div><span className="disc-seg-lbl">Format</span><span className="disc-seg-val">Billboard</span></div>
                    </div>
                    <div className="disc-seg-div" />
                    <div className="disc-seg" style={{ flex: 1.2 }}>
                      <i className="ph ph-calendar" style={{ fontSize: 12, color: 'var(--b500)' }} />
                      <div><span className="disc-seg-lbl">Dates</span><span className="disc-seg-val">Apr 1–30</span></div>
                    </div>
                    <div className="disc-search-btn"><i className="ph-fill ph-magnifying-glass" /></div>
                  </div>

                  {/* Filter chips */}
                  <div className="disc-chips">
                    <span className="disc-chip"><i className="ph ph-circle" style={{ fontSize: 6 }} /> Available</span>
                    <span className="disc-chip">Under ₹50K</span>
                    <span className="disc-chip">Billboard</span>
                    <span className="disc-chip">Bengaluru</span>
                  </div>

                  {/* 3 site cards */}
                  <div className="disc-cards">
                    {DISCOVERY_CARDS.map((c) => (
                      <div className={`disc-card${c.selected ? ' selected' : ''}`} key={c.name}>
                        <div className="disc-thumb" style={{ backgroundImage: `url(${c.img})` }}>
                          {c.selected && (
                            <div className="disc-added">
                              <i className="ph-fill ph-check-circle" />
                              <span>ADDED TO<br />CAMPAIGN</span>
                            </div>
                          )}
                        </div>
                        <div className="disc-info">
                          <div className="disc-name">{c.name}</div>
                          <div className="disc-meta">{c.meta}</div>
                          <div className="disc-card-tags">
                            <span className="disc-card-tag">{c.status === 'available' ? 'Available' : 'Booked'}</span>
                          </div>
                          <div className="disc-price-row">
                            <span className="disc-price">{c.price}</span>
                            <i className="ph ph-caret-right" aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="disc-count">Showing 3 of 68 spaces in Bengaluru</div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          ESCROW — Advertiser perspective
      ════════════════════════════════════════════════ */}
      <section className="escrow sec" id="escrow" aria-labelledby="escrow-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 620 }}>
            <p className="eyebrow">How money moves</p>
            <h2 className="sec-title" id="escrow-hl" style={{ color: '#fff' }}>
              Your payment is protected from the moment you book.
            </h2>
            <p className="sec-desc" style={{ color: 'var(--lumen-slate-400)' }}>
              FlipAd never transfers your money to the space owner until your campaign
              has run and proof of display is verified. If it doesn&apos;t happen, you get it back.
            </p>
          </div>

          <div className="esc-scroll-hint" aria-hidden="true">
            <i className="ph ph-arrows-left-right" style={{ fontSize: 14 }} />
            Scroll to see full payment flow
          </div>

          {/* Flow diagram */}
          <div
            className="esc-diag reveal"
            id="esc-diag"
            ref={escDiagRef}
            role="img"
            aria-label="How FlipAd escrow works: advertiser books, payment locked in escrow, campaign runs, proof submitted, funds released to host"
          >
            <div className="esc-stepper">
              <div className="esc-stp esc-stp-1">
                <div className="esc-stp-icon"><i className="ph ph-user-circle" aria-hidden="true" /></div>
                <div className="esc-stp-title">You Book</div>
                <div className="esc-stp-sub">Select space<br />&amp; dates</div>
              </div>
              <div className="esc-conn esc-conn-1 blue" aria-hidden="true">
                <div className="esc-conn-track"><div className="esc-conn-line" /><div className="esc-conn-tip" /></div>
                <div className="esc-conn-lbl">₹ PAYMENT</div>
              </div>
              <div className="esc-stp esc-stp-2 is-escrow">
                <div className="esc-stp-icon"><i className="ph ph-vault" aria-hidden="true" /></div>
                <div className="esc-stp-title">Funds Locked</div>
                <div className="esc-stp-sub">Held by FlipAd<br />Protected · Escrow</div>
              </div>
              <div className="esc-conn esc-conn-2 blue" aria-hidden="true">
                <div className="esc-conn-track"><div className="esc-conn-line" /><div className="esc-conn-tip" /></div>
                <div className="esc-conn-lbl">CAMPAIGN START</div>
              </div>
              <div className="esc-stp esc-stp-3">
                <div className="esc-stp-icon"><i className="ph ph-megaphone" aria-hidden="true" /></div>
                <div className="esc-stp-title">Campaign Runs</div>
                <div className="esc-stp-sub">Your ad goes live<br />Dates confirmed</div>
              </div>
              <div className="esc-conn esc-conn-3 blue" aria-hidden="true">
                <div className="esc-conn-track"><div className="esc-conn-line" /><div className="esc-conn-tip" /></div>
                <div className="esc-conn-lbl">PROOF</div>
              </div>
              <div className="esc-stp esc-stp-4">
                <div className="esc-stp-icon"><i className="ph ph-camera" aria-hidden="true" /></div>
                <div className="esc-stp-title">Proof Verified</div>
                <div className="esc-stp-sub">Timestamped photo<br />Reviewed by FlipAd</div>
              </div>
              <div className="esc-conn esc-conn-4 green" aria-hidden="true">
                <div className="esc-conn-track"><div className="esc-conn-line" /><div className="esc-conn-tip" /></div>
                <div className="esc-conn-lbl">ON PROOF</div>
              </div>
              <div className="esc-stp esc-stp-5">
                <div className="esc-stp-icon"><i className="ph ph-check-circle" aria-hidden="true" /></div>
                <div className="esc-stp-title">You Receive Report</div>
                <div className="esc-stp-sub">Campaign closed<br />Evidence in hand</div>
              </div>
            </div>
          </div>

          {/* Advertiser-framed guarantee cards */}
          <div className="escrow-guarantees">

            <div className="escrow-g adv reveal" data-d="1">
              <div className="escrow-g-icon adv" aria-hidden="true"><i className="ph ph-lock-laminated" /></div>
              <div className="escrow-g-title">Your payment is protected from day one.</div>
              <div className="escrow-g-desc">
                The moment you confirm a booking, your payment is held in FlipAd escrow —
                not transferred to the space owner. It only moves after your campaign runs
                and verified proof of display is submitted. If the campaign doesn&apos;t happen,
                you get your money back. Full stop.
              </div>
            </div>

            <div className="escrow-g host reveal" data-d="2">
              <div className="escrow-g-icon host" aria-hidden="true"><i className="ph ph-seal-check" /></div>
              <div className="escrow-g-title">Proof of display on every campaign.</div>
              <div className="escrow-g-desc">
                Every campaign closes with a timestamped photo uploaded by the space owner
                and independently reviewed by FlipAd. You receive a campaign completion
                report with proof attached. Your finance team has documentation.
                Your stakeholders have evidence.
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          FORMAT SUPPORT
      ════════════════════════════════════════════════ */}
      <section className="adv-formats sec" id="formats" aria-labelledby="formats-hl">
        <div className="container">
          <div className="sec-header center reveal" style={{ maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
            <p className="eyebrow">Formats on FlipAd</p>
            <h2 className="sec-title" id="formats-hl">Every format across India.</h2>
          </div>

          <div className="adv-fmts-grid">
            {FORMAT_TILES.map((f, i) => (
              <Link to="/#formats" className="adv-fmt-tile reveal" data-d={String((i % 3) + 1)} key={f.name}>
                <i className={`ph ${f.icon} adv-fmt-icon`} aria-hidden="true" />
                <div className="adv-fmt-name">{f.name}</div>
                <p className="adv-fmt-desc">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          FAQ
      ════════════════════════════════════════════════ */}
      <section className="so-faq sec" id="faq" aria-labelledby="faq-hl">
        <div className="container">
          <div className="faq-layout">

            <div className="faq-main">
              <div className="sec-header reveal" style={{ maxWidth: 560, marginBottom: 'var(--lumen-space-10)' }}>
                <p className="eyebrow">Common questions</p>
                <h2 className="sec-title" id="faq-hl">Everything you need to know about booking on FlipAd.</h2>
              </div>

              <div className="so-faq-list reveal">
                {FAQ_ITEMS.map((item, i) => (
                  <FaqItem
                    item={item}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq((prev) => (prev === i ? null : i))}
                    key={item.q}
                  />
                ))}
              </div>
            </div>

            <aside className="faq-side reveal" data-d="2">
              <div className="faq-card">
                <span className="faq-card-badge"><i className="ph-fill ph-circle" aria-hidden="true" />Avg. reply under 2 hours</span>
                <h3 className="faq-card-title">Still have a question?</h3>
                <p className="faq-card-desc">Real people, not bots. Reach out and we&apos;ll walk you through pricing, escrow, or a specific site.</p>
                <div className="faq-card-actions">
                  <a href="mailto:support@flipad.in" className="lmn-btn secondary sm"><i className="ph ph-envelope-simple" aria-hidden="true" />Email support</a>
                  <a href="#" className="lmn-btn primary sm" onClick={(e) => e.preventDefault()}><i className="ph ph-whatsapp-logo" aria-hidden="true" />Chat on WhatsApp</a>
                </div>
              </div>
              <div className="faq-trust">
                <div className="faq-trust-item"><i className="ph ph-map-pin" aria-hidden="true" /><span><strong>1,240+</strong>&nbsp;verified spaces</span></div>
                <div className="faq-trust-item"><i className="ph ph-shield-check" aria-hidden="true" /><span><strong>100%</strong>&nbsp;escrow-protected</span></div>
                <div className="faq-trust-item"><i className="ph ph-buildings" aria-hidden="true" /><span><strong>23</strong>&nbsp;cities covered</span></div>
              </div>
            </aside>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          CLOSING CTA
      ════════════════════════════════════════════════ */}
      <section className="so-cta sec" id="cta" aria-labelledby="cta-hl">
        <div className="container">
          <div className="reveal">
            <p className="eyebrow">Get started</p>
            <h2 className="so-cta-hl" id="cta-hl">Ready to book your first OOH campaign?</h2>
            <p className="so-cta-sub">1,240+ verified spaces across 23 cities. Browse for free, book in minutes.</p>
            <div className="so-cta-btns">
              <button
                type="button"
                className="lmn-btn lg nav-auth-trigger"
                style={{ background: '#fff', borderColor: '#fff', color: 'var(--b900)' }}
                onClick={openPlayStore}
              >
                Browse inventory <i className="ph ph-arrow-right" aria-hidden="true" />
              </button>
            </div>
            <p className="so-cta-link">
              Have questions? <a href="mailto:support@flipad.in" style={{ color: 'color-mix(in srgb,#fff 60%,transparent)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>Contact us →</a>
            </p>
            <p className="so-cta-link" style={{ marginTop: 'var(--lumen-space-3)' }}>
              Own a space? <Link to="/space-owners" style={{ color: 'color-mix(in srgb,#fff 60%,transparent)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>List it on FlipAd →</Link>
            </p>
          </div>
        </div>
      </section>

      <Footer discoverSpacesHref="#" discoverSpacesLabel="Browse spaces" escrowHref="#escrow" />
    </>
  );
}
